const LIFETIME_TOTAL = 50;
const HISTORICAL_LIFETIME_SALES = 30;
const FALLBACK_REMAINING = Math.max(0, LIFETIME_TOTAL - HISTORICAL_LIFETIME_SALES);
const AVAILABILITY_API_URL = import.meta.env.VITE_LIFETIME_AVAILABILITY_API_URL || '';
const POLL_INTERVAL_MS = 60_000;

let currentRemaining = FALLBACK_REMAINING;
let currentIsLive = false;

function clampRemaining(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(LIFETIME_TOTAL, Math.round(numeric)));
}

function remainingFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const direct = [
    payload.remaining,
    payload.lifetimeRemaining,
    payload.lifetime_remaining,
    payload.available,
  ];

  for (const candidate of direct) {
    const parsed = clampRemaining(candidate);
    if (parsed !== null) return parsed;
  }

  const soldCandidates = [
    payload.sold,
    payload.lifetimeSold,
    payload.lifetime_sold,
  ];

  for (const candidate of soldCandidates) {
    const sold = Number(candidate);
    if (Number.isFinite(sold)) return clampRemaining(LIFETIME_TOTAL - sold);
  }

  return null;
}

function findLifetimeCard() {
  const pricing = document.getElementById('pricing');
  if (!pricing) return null;

  const heading = [...pricing.querySelectorAll('h3')].find((node) =>
    /lifetime/i.test(node.textContent || '')
  );

  return heading?.parentElement?.parentElement || null;
}

function buildAvailabilityPanel() {
  const panel = document.createElement('div');
  panel.dataset.lifetimeAvailability = 'true';
  panel.setAttribute('role', 'status');
  panel.setAttribute('aria-live', 'polite');
  panel.style.marginBottom = '1.5rem';
  panel.style.padding = '0.95rem 1rem';
  panel.style.borderRadius = '1rem';
  panel.style.border = '1px solid #f6c76d';
  panel.style.background = '#fffaf0';
  panel.style.boxShadow = '0 8px 24px rgba(180, 83, 9, 0.08)';

  const top = document.createElement('div');
  top.style.display = 'flex';
  top.style.alignItems = 'center';
  top.style.justifyContent = 'space-between';
  top.style.gap = '0.75rem';
  top.style.flexWrap = 'wrap';

  const label = document.createElement('span');
  label.dataset.lifetimeLabel = 'true';
  label.textContent = 'Limited lifetime allocation';
  label.style.fontFamily = 'var(--font-body)';
  label.style.fontSize = '12px';
  label.style.fontWeight = '600';
  label.style.letterSpacing = '0.08em';
  label.style.textTransform = 'uppercase';
  label.style.color = '#b45309';

  const source = document.createElement('span');
  source.dataset.lifetimeSource = 'true';
  source.style.fontFamily = 'var(--font-body)';
  source.style.fontSize = '12px';
  source.style.fontWeight = '600';
  source.style.color = '#047857';

  const remaining = document.createElement('div');
  remaining.dataset.lifetimeRemaining = 'true';
  remaining.style.marginTop = '0.55rem';
  remaining.style.fontFamily = 'var(--font-heading)';
  remaining.style.fontSize = '20px';
  remaining.style.fontWeight = '700';
  remaining.style.lineHeight = '1.25';
  remaining.style.color = '#0f172a';

  const detail = document.createElement('div');
  detail.dataset.lifetimeDetail = 'true';
  detail.style.marginTop = '0.3rem';
  detail.style.fontFamily = 'var(--font-body)';
  detail.style.fontSize = '14px';
  detail.style.lineHeight = '1.5';
  detail.style.color = '#64748b';

  const track = document.createElement('div');
  track.style.marginTop = '0.8rem';
  track.style.height = '6px';
  track.style.overflow = 'hidden';
  track.style.borderRadius = '999px';
  track.style.background = '#e2e8f0';

  const fill = document.createElement('div');
  fill.dataset.lifetimeProgress = 'true';
  fill.style.height = '100%';
  fill.style.borderRadius = '999px';
  fill.style.background = 'linear-gradient(90deg, #f59e0b, #fbbf24)';
  fill.style.transition = 'width 300ms ease';

  track.appendChild(fill);
  top.append(label, source);
  panel.append(top, remaining, detail, track);
  return panel;
}

function setTextIfChanged(node, text) {
  if (node && node.textContent !== text) node.textContent = text;
}

function updateAvailabilityPanel(card, remainingCount, isLive) {
  if (!card) return;

  const remaining = clampRemaining(remainingCount) ?? FALLBACK_REMAINING;
  const sold = LIFETIME_TOTAL - remaining;
  const claimedPercent = Math.min(100, Math.max(0, (sold / LIFETIME_TOTAL) * 100));

  let panel = card.querySelector('[data-lifetime-availability="true"]');
  if (!panel) {
    panel = buildAvailabilityPanel();
    const priceBlock = card.children[1];
    if (priceBlock) card.insertBefore(panel, priceBlock);
    else card.appendChild(panel);
  }

  const remainingNode = panel.querySelector('[data-lifetime-remaining="true"]');
  const detailNode = panel.querySelector('[data-lifetime-detail="true"]');
  const progressNode = panel.querySelector('[data-lifetime-progress="true"]');
  const sourceNode = panel.querySelector('[data-lifetime-source="true"]');

  setTextIfChanged(
    remainingNode,
    remaining > 0
      ? `${remaining} of ${LIFETIME_TOTAL} lifetime licences remaining`
      : 'Lifetime allocation sold out'
  );
  if (remainingNode) remainingNode.style.color = remaining <= 5 ? '#b91c1c' : '#0f172a';

  setTextIfChanged(detailNode, `${sold} of ${LIFETIME_TOTAL} lifetime licences claimed`);
  if (progressNode) progressNode.style.width = `${claimedPercent}%`;
  setTextIfChanged(sourceNode, isLive ? 'Live availability' : '');

  const cta = [...card.querySelectorAll('a')].find((node) => /choose/i.test(node.textContent || ''));
  if (cta) {
    if (!cta.dataset.originalHref) cta.dataset.originalHref = cta.getAttribute('href') || '';
    if (!cta.dataset.originalText) cta.dataset.originalText = cta.textContent || 'Choose Lifetime Plan';

    if (remaining === 0) {
      cta.removeAttribute('href');
      cta.removeAttribute('target');
      cta.setAttribute('aria-disabled', 'true');
      cta.style.pointerEvents = 'none';
      cta.style.opacity = '0.55';
      setTextIfChanged(cta, 'Lifetime allocation sold out');
    } else if (cta.getAttribute('aria-disabled') === 'true') {
      cta.setAttribute('href', cta.dataset.originalHref || '');
      cta.setAttribute('target', '_blank');
      cta.removeAttribute('aria-disabled');
      cta.style.pointerEvents = '';
      cta.style.opacity = '';
      setTextIfChanged(cta, cta.dataset.originalText || 'Choose Lifetime Plan');
    }
  }
}

function renderCurrentAvailability() {
  updateAvailabilityPanel(findLifetimeCard(), currentRemaining, currentIsLive);
}

async function fetchLiveAvailability() {
  if (!AVAILABILITY_API_URL) return null;

  let timeout;
  try {
    const controller = new AbortController();
    timeout = window.setTimeout(() => controller.abort(), 5000);
    const response = await fetch(`${AVAILABILITY_API_URL}${AVAILABILITY_API_URL.includes('?') ? '&' : '?'}_ts=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) return null;
    return remainingFromPayload(await response.json());
  } catch {
    return null;
  } finally {
    if (timeout) window.clearTimeout(timeout);
  }
}

async function refreshLiveAvailability() {
  const liveRemaining = await fetchLiveAvailability();
  if (liveRemaining === null) return;
  currentRemaining = liveRemaining;
  currentIsLive = true;
  renderCurrentAvailability();
}

export function startLifetimeAvailability() {
  const observer = new MutationObserver(() => {
    const card = findLifetimeCard();
    if (card && !card.querySelector('[data-lifetime-availability="true"]')) {
      renderCurrentAvailability();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  requestAnimationFrame(() => {
    renderCurrentAvailability();
    refreshLiveAvailability();
  });

  const pollId = AVAILABILITY_API_URL
    ? window.setInterval(refreshLiveAvailability, POLL_INTERVAL_MS)
    : null;

  return () => {
    observer.disconnect();
    if (pollId) window.clearInterval(pollId);
  };
}

export const lifetimeAvailabilityDefaults = Object.freeze({
  total: LIFETIME_TOTAL,
  historicalSales: HISTORICAL_LIFETIME_SALES,
  remaining: FALLBACK_REMAINING,
});